import React, { useCallback, useState } from "react";
import toast from "react-hot-toast";
import TransactionHistoryRefreshButton from "../../components/TransactionHistoryRefreshButton";
import { errorMessage } from "../../utils/errorMessage";
import type { ActivityFilter } from "../../utils/activity/filters";
import {
  useRefreshTransactions,
  useTransactions,
} from "../../hooks/queries/useTransactions";
import {
  ACTIVITY_PAGE_LIMIT,
  ACTIVITY_POLL_INTERVAL,
} from "../../hooks/queries/activityKeys";
import ActivityList from "./ActivityList";

/**
 * One tab's list. Owns its own query, so each type paginates and refreshes
 * independently and switching tabs never re-fetches what's already cached.
 */
const ActivityTypePanel = ({
  filter,
  hideAmounts,
  onSelect,
  limit = ACTIVITY_PAGE_LIMIT,
}: {
  filter: ActivityFilter;
  hideAmounts?: boolean;
  onSelect: (raw: any) => void;
  limit?: number;
}) => {
  const {
    data,
    isPending,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError,
    error,
  } = useTransactions(filter, {
    limit,
    refetchInterval: ACTIVITY_POLL_INTERVAL,
  });

  const refreshTransactions = useRefreshTransactions(filter, limit);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const onRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refreshTransactions();
    } finally {
      setIsRefreshing(false);
    }
  }, [refreshTransactions]);

  const onEndReached = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  React.useEffect(() => {
    if (isError) {
      toast.error(
        errorMessage((error as any)?.message || (error as any)?.data?.message)
      );
    }
  }, [isError, error]);

  return (
    <div className="w-full">
      <div className="w-full flex justify-end mt-2">
        <TransactionHistoryRefreshButton
          onRefresh={onRefresh}
          isRefreshing={isRefreshing}
        />
      </div>
      <ActivityList
        items={data?.items ?? []}
        isLoading={isPending}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        onEndReached={onEndReached}
        hideAmounts={hideAmounts}
        onSelect={onSelect}
      />
    </div>
  );
};

export default React.memo(ActivityTypePanel);
